import { usePortalMetadataQuery } from '@/lib/data-hooks';
import { isNotFound } from '@tanstack/react-router';
import type { HTMLAttributes } from 'react';
import { useCampaignDisplayContext } from './campaign-display-context';
import { usePortalDisplayContext } from './portal-display-context';

/* Same values as `--foreground` and `--primary-foreground` in the design system */
const DARK_FOREGROUND = 'oklch(0.145 0 0)';
const LIGHT_FOREGROUND = 'oklch(0.985 0 0)';

function normalizePrimaryColor(color: string) {
	return color === '#ffffff' || color === '#fdfdfd' ? '#000000' : color;
}

function channelLuminance(color: string, offset: number) {
	const value = Number.parseInt(color.slice(offset, offset + 2), 16) / 255;
	return value <= 0.04045 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
}

/**
 * Light portal colors (Glovo's yellow, for instance) leave the default white text on top of them
 * unreadable. Portals keep their white text wherever it is legible, and only the colors where it
 * fails fall back to dark text. 3:1 is the WCAG AA ratio for large text and UI components, which
 * is what sits on the portal color: buttons, chips, the footer.
 */
function primaryForegroundColor(color: string) {
	if (!/^#[0-9a-f]{6}$/i.test(color)) return LIGHT_FOREGROUND;

	const luminance =
		0.2126 * channelLuminance(color, 1) + 0.7152 * channelLuminance(color, 3) + 0.0722 * channelLuminance(color, 5);
	const whiteContrast = 1.05 / (luminance + 0.05);

	return whiteContrast < 3 ? DARK_FOREGROUND : LIGHT_FOREGROUND;
}

function primaryColorStyle(color: string) {
	const primaryColor = normalizePrimaryColor(color);

	return {
		'--primary': primaryColor,
		'--primary-foreground': primaryForegroundColor(primaryColor),
		'--ring': primaryColor,
	} as React.CSSProperties;
}

export function DivWithPrimaryColorVariable(props: HTMLAttributes<HTMLDivElement>) {
	const displayData = useCampaignDisplayContext();
	const portalData = usePortalDisplayContext();

	if (displayData) {
		return <div {...props} style={primaryColorStyle(displayData.metadata.color)} />;
	}
	if (portalData) {
		return <div {...props} style={primaryColorStyle(portalData.color)} />;
	}
	return <DivFromMetadata {...props} />;
}

function DivFromMetadata(props: HTMLAttributes<HTMLDivElement>) {
	const metadata = usePortalMetadataQuery();

	if (isNotFound(metadata)) return <div {...props} />;

	return <div {...props} style={primaryColorStyle(metadata.color)} />;
}
